import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, Row } from 'antd';
import { useAuthService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { notificationType } from '../../common/constants/notificationType';
import { routesPath } from '../../common/constants/routesPath';
import { fontSize, iconColor } from '../../theming';
import getInputRules, { validateFullName, inputType } from '../../utils/getInputRules';
import classes from '../../common/styles/FormStyles.module.scss';
import ReactGA from 'react-ga4';
import { googleAnalyticsEvents } from '../../common/constants/googleAnalyticsEvents';
import dayjs from 'dayjs';
import { redirectWithSearchParams } from '../../utils/redirectWithSearchParams';
import ReCAPTCHA from 'react-google-recaptcha';
import { constants } from '../../common/constants/constants';

const SignUpForm = ({ openNotification, setActiveTabKey, t, marketingConsent = false, consentField = null }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setAuth } = useAuth();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [formValid, setFormValid] = useState(true);
    const { signUpEmailPassword } = useAuthService();
    const isInitialMount = useRef(true); // to track initial render and avoid validation on first render
    const [startTime, setStartTime] = useState(new Date());

    useEffect(() => {
        ReactGA.event(googleAnalyticsEvents.SIGNUP_INITIATED, {
            start_time: dayjs(startTime).format('DD/MM/YY - HH:mm'),
        });
    }, []);

    const recaptchaRef = useRef();

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        form.validateFields()
            .then(() => setFormValid(true))
            .catch(() => setFormValid(false));
    }, [formValid]);

    const onFormSubmit = async event => {
        const isFormValid = await form
            .validateFields()
            .then(() => true)
            .catch(() => false);

        if (!isFormValid) {
            setFormValid(false);
            openNotification({
                type: notificationType.ERROR,
                message: t('notification.register-form-validation-error.message'),
                description: t('notification.register-form-validation-error.description'),
                placement: 'top',
            });
            return;
        }

        try {
            setLoading(true);
            const recaptchaToken = await recaptchaRef.current?.getValue();
            const browserLanguage = navigator.language.slice(0, 2);
            const userTokens = await signUpEmailPassword(
                {
                    firstName: event.fullName.split(' ')[0],
                    lastName: event.fullName.split(' ')[1],
                    email: event.email,
                    password: event.password,
                    language: browserLanguage,
                    // the API counts only a literal true; unticked is sent as false and records nothing
                    marketingConsent: marketingConsent === true,
                },
                recaptchaToken
            );

            if (userTokens?.data) {
                const { accessToken, refreshToken } = userTokens.data;

                setAuth(accessToken, refreshToken);

                redirectWithSearchParams(navigate, searchParams, routesPath.ROOT.DASHBOARD);
            }

            const timeElapsedInSeconds = Math.round((new Date() - startTime) / 1000);

            ReactGA.event(googleAnalyticsEvents.SIGNUP_COMPLETED, {
                total_seconds: timeElapsedInSeconds,
            });

            setLoading(false);
        } catch (error) {
            const errorCode = error.response.data?.code;
            openNotification({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errorCode}.message`),
                description: t(`apiResponses:${errorCode}.description`),
                placement: 'top',
            });
            setLoading(false);
            setActiveTabKey(error.response.data?.redirectToSignUp ? '1' : '0');
        }
    };

    return (
        <Form form={form} onFinish={onFormSubmit} style={{ width: 'clamp(300px, 85vw, 400px)' }} autoComplete="off">
            <Form.Item
                className={classes['form-item']}
                label={<span className={classes['form-label']}>{t('auth:login-label-email')}</span>}
                name="email"
                rules={getInputRules(t, inputType.EMAIL, t('form:errors.required'), t('form:errors.email'))}
                style={{ marginBottom: '18px' }}
                labelCol={{ span: 24 }}
            >
                <Input
                    className={classes['input-login']}
                    placeholder={t('auth:placeholder.enter-email')}
                    prefix={
                        <i
                            className="fi-rr-envelope"
                            style={{ color: iconColor.signin, fontSize: '18px', marginRight: '12px' }}
                        />
                    }
                />
            </Form.Item>
            <Form.Item
                className={classes['form-item']}
                label={<span className={classes['form-label']}>{t('auth:register-label-full-name')}</span>}
                name="fullName"
                rules={[
                    {
                        validator: validateFullName,
                    },
                ]}
                labelCol={{ span: 24 }}
                style={{ marginBottom: '18px' }}
            >
                <Input
                    className={classes['input-login']}
                    placeholder={t('auth:placeholder.enter-full-name')}
                    prefix={
                        <i
                            className="fi-rr-user"
                            style={{ fontSize: '18px', color: iconColor.signin, marginRight: '12px' }}
                        />
                    }
                    size="large"
                />
            </Form.Item>

            <Form.Item
                className={classes['form-item']}
                label={<span className={classes['form-label']}>{t('auth:register-label-password')}</span>}
                name="password"
                rules={getInputRules(t, inputType.PASSWORD, t('form-error.password-required'))}
                hasFeedback
                labelCol={{ span: 24 }}
                style={{ marginBottom: '18px' }}
            >
                <Input.Password
                    className={classes['input-login']}
                    placeholder={t('auth:placeholder.enter-password')}
                    prefix={
                        <i
                            className="fi-rr-lock"
                            style={{ fontSize: '18px', color: iconColor.signin, marginRight: '12px' }}
                        />
                    }
                />
            </Form.Item>

            <Form.Item
                className={classes['form-item']}
                label={<span className={classes['form-label']}>{t('auth:register-label-password-confirm')}</span>}
                name="confirm"
                dependencies={['password']}
                hasFeedback
                rules={[
                    {
                        required: true,
                        message: t('form-error.password-confirm'),
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error(t('form-error.password-match')));
                        },
                    }),
                ]}
                labelCol={{ span: 24 }}
            >
                <Input.Password
                    className={classes['input-login']}
                    placeholder={t('auth:placeholder.enter-password-confirm')}
                    prefix={
                        <i
                            className="fi-rr-lock"
                            style={{
                                fontSize: fontSize.md,
                                color: iconColor.signin,
                                marginRight: '12px',
                            }}
                        />
                    }
                />
            </Form.Item>

            <Form.Item
                style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}
                name="recaptcha"
                rules={getInputRules(t, inputType.RECAPTCHA)}
            >
                <ReCAPTCHA ref={recaptchaRef} sitekey={constants.RECAPTCHA_SITE_KEY} />
            </Form.Item>

            {consentField}

            <Row justify="center" align="center">
                <Button
                    className={classes.signin}
                    loading={loading}
                    htmlType="submit"
                    style={{
                        marginTop: '20px',
                    }}
                    type="primary"
                    size="large"
                    disabled={!formValid}
                >
                    {t('buttons.register')}
                </Button>
            </Row>
        </Form>
    );
};

export default SignUpForm;
